import apiService from './api';
import type { ApiResponse } from '../types/api';
import type {
  CreateQuarantineTreatmentPayload,
  LoadQuarantine,
  LotQuarantineEntry,
  QuarantineProtocol,
  QuarantineProtocolPayload,
  SprayQuarantine,
} from '../types/nurseryQuarantine';

/**
 * Quarantine protocols. Anyone who works the nursery reads them; a nursery
 * manager sets them up. The treatment itself is a spray (ICA plan).
 */
class NurseryQuarantineService {
  async getProtocols(includeInactive = false): Promise<ApiResponse<QuarantineProtocol[]>> {
    return apiService.get(`/nursery/quarantine-protocols${includeInactive ? '?include_inactive=true' : ''}`);
  }

  async createProtocol(data: QuarantineProtocolPayload): Promise<ApiResponse<QuarantineProtocol>> {
    return apiService.post('/nursery/quarantine-protocols', data);
  }

  async updateProtocol(id: number, data: QuarantineProtocolPayload): Promise<ApiResponse<QuarantineProtocol>> {
    return apiService.put(`/nursery/quarantine-protocols/${id}`, data);
  }

  /** The organisation's people, names only, to choose a certification controller from. Nursery manager. */
  async getPeople(): Promise<ApiResponse<{ id: number; name: string }[]>> {
    return apiService.get('/nursery/quarantine-people');
  }

  /** What a load needs under each protocol, and the treatments made for it. */
  async getLoadQuarantine(loadId: number): Promise<ApiResponse<LoadQuarantine>> {
    return apiService.get(`/nursery/loads/${loadId}/quarantine`);
  }

  /** Make a treatment: a spray at the designated area, the mix locked to the protocol. Spray creator seat. */
  async createTreatment(loadId: number, data: CreateQuarantineTreatmentPayload): Promise<ApiResponse<{
    treatment_id: number; spray_application_id: number; reference_number: string;
  }>> {
    return apiService.post(`/nursery/loads/${loadId}/quarantine/treatments`, data);
  }

  /** One PHAC number per stop. Nursery manager. */
  async setPhac(loadId: number, orderId: number, phacNumber: string | null): Promise<ApiResponse<{ order_id: number; phac_number: string | null }>> {
    return apiService.put(`/nursery/loads/${loadId}/orders/${orderId}/phac`, { phac_number: phacNumber ?? '' });
  }

  /** Fill in a crop type's botanical name where it has none. Nursery manager. */
  async setCropTypeScientificName(cropTypeId: number, scientificName: string): Promise<ApiResponse<{ id: number; scientific_name: string }>> {
    return apiService.put(`/nursery/crop-types/${cropTypeId}/scientific-name`, { scientific_name: scientificName });
  }

  async getTreatmentRecordPdf(treatmentId: number): Promise<Blob> {
    return apiService.get(`/nursery/quarantine-treatments/${treatmentId}/record.pdf`, { responseType: 'blob' }) as unknown as Promise<Blob>;
  }

  async getSpeciesPdf(loadId: number): Promise<Blob> {
    return apiService.get(`/nursery/loads/${loadId}/species.pdf`, { responseType: 'blob' }) as unknown as Promise<Blob>;
  }

  async getQuarantinePackPdf(loadId: number): Promise<Blob> {
    return apiService.get(`/nursery/loads/${loadId}/quarantine-pack.pdf`, { responseType: 'blob' }) as unknown as Promise<Blob>;
  }

  /** The quarantine treatments a lot went out under, newest first. */
  async getLotQuarantine(lotId: number): Promise<ApiResponse<LotQuarantineEntry[]>> {
    return apiService.get(`/nursery/lots/${lotId}/quarantine`);
  }

  /** The quarantine treatment a spray carries out; null when it is not one. */
  async getSprayQuarantine(sprayId: number): Promise<ApiResponse<SprayQuarantine | null>> {
    return apiService.get(`/nursery/sprays/${sprayId}/quarantine`);
  }

  /** Sign a customer order off as treated. Spray seat; the spray must have started. */
  async signOffOrder(treatmentId: number, orderId: number, notes?: string | null): Promise<ApiResponse<SprayQuarantine>> {
    return apiService.post(`/nursery/quarantine-treatments/${treatmentId}/orders/${orderId}/sign-off`, { notes: notes ?? null });
  }

  /** Take a sign-off back, before the treatment is validated. */
  async undoSignOffOrder(treatmentId: number, orderId: number): Promise<ApiResponse<SprayQuarantine>> {
    return apiService.delete(`/nursery/quarantine-treatments/${treatmentId}/orders/${orderId}/sign-off`);
  }

  /** Validate a finished treatment. Never by its operator, nor anyone who signed its orders off. */
  async validateTreatment(id: number): Promise<ApiResponse<{ id: number; load_id: number }>> {
    return apiService.post(`/nursery/quarantine-treatments/${id}/validate`, {});
  }
}

export const nurseryQuarantineService = new NurseryQuarantineService();
export default nurseryQuarantineService;
